import { useEffect } from 'react'
import Clarity from '@microsoft/clarity'
import { identifySentryUser, clearSentryUser } from '@/lib/sentry-context'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'

function identifyClarityUser(userId: string | null, email?: string | null) {
  if (!import.meta.env.PROD) return
  if (!userId) return
  try {
    Clarity.identify(userId, undefined, undefined, email ?? undefined)
  } catch {
    // SDK ainda não inicializado — silencioso
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const setSupabaseUser = useAuthStore((state) => state.auth.setSupabaseUser)
  const setRoles = useAuthStore((state) => state.auth.setRoles)
  const setIsLoading = useAuthStore((state) => state.auth.setIsLoading)

  useEffect(() => {
    // Check current session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null
      setSupabaseUser(user)
      const roles: string[] = user?.app_metadata?.roles || []
      setRoles(roles)
      setIsLoading(false)
      identifyClarityUser(user?.id ?? null, user?.email)
      if (user) {
        identifySentryUser(
          { id: user.id, email: user.email },
          user.app_metadata?.tenant_id ?? null,
        )
      } else {
        clearSentryUser()
      }
    })

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const user = session?.user ?? null
        setSupabaseUser(user)
        const roles: string[] = user?.app_metadata?.roles || []
        setRoles(roles)
        setIsLoading(false)
        identifyClarityUser(user?.id ?? null, user?.email)
        if (user) {
          identifySentryUser(
            { id: user.id, email: user.email },
            user.app_metadata?.tenant_id ?? null,
          )
        } else {
          clearSentryUser()
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [setSupabaseUser, setRoles, setIsLoading])

  return <>{children}</>
}
